import React, { Suspense, useEffect, useRef } from 'react';
import {BrowserRouter, Navigate, Route, Routes} from "react-router-dom";
import { useAppDispatch, useAppSelector } from './store';
import {FirstPage} from "./components/FirstPage";
import {COOKIES, DEV_FINAL_VALIDATION_FLOW, DEV_FINAL_THANKYOU_FLOW, DEV_PENDING_FLOW, FINAL, INFO, OFFERS, PENDING, AUTO_REGISTER, UNSUBSCRIBE, SUCCESS, BENEFICIOS, OFFERWALL_LANDING, SAFE_LANDING, RATE_LANDING, PIZZA_LANDING, DRUMUL_SCURT_LANDING, LEAD_OFFERS} from "./constants/constantApi";
import { getFormLink } from "./utils/getTld";
import { getGeneralConfig } from './store/features/configsSlice/configsSlice';
import { getFormsConfig } from './store/features/formSlice/formSlice';
import { setClickIDHandler } from './store/features/userSlice/utils';
import { sendQueryParams } from './api/methods/sendUserAction/handlers';
import { setAffiliateId } from './store/features/mainData/mainDataSlice';
import { resolveAffiliateFromSearch } from './utils/resolveAffiliateFromUrl';
import { rememberTrafficSource } from './utils/trafficSource';
import { LandingViewTracker } from './components/LandingViewTracker';
import { ScrollDepthTracker } from './components/ScrollDepthTracker';
import { EngagementTimeTracker } from './components/EngagementTimeTracker';
import { SpentTimeTracker } from './components/SpentTimeTracker';

import { Layout } from './components/Layuot/Layout';
import { Preloader } from './UI/Preloader';
import { setSiteMeta } from "./utils/setSiteMeta";
import { trackResourceLoadError, trackInAppBrowserIssue } from "./utils/redirectMonitoring";
import { lazyWithReload } from "./utils/lazyWithReload";
import { trackRedirect } from "./utils/trackNavigation";

const CustomFormContainer = lazyWithReload(() => import("./components/CustomForm/CustomFormContainer").then(m => ({ default: m.CustomFormContainer })));
const Congratulation = lazyWithReload(() => import("./components/Congratulation").then(m => ({ default: m.Congratulation })));
import { OfferwallRedirect } from "./components/OfferwallRedirect";
const FinalPage = lazyWithReload(() => import("./components/FinalPage/FinalPage").then(m => ({ default: m.FinalPage })));
const Informance = lazyWithReload(() => import("./components/Informace/Informance").then(m => ({ default: m.Informance })));
const Cookies = lazyWithReload(() => import("./components/Cookies/Cookies").then(m => ({ default: m.Cookies })));
const AutoRegister = lazyWithReload(() => import("./components/AutoRegister").then(m => ({ default: m.AutoRegister })));
const DevFinalValidationFlow = lazyWithReload(() => import("./components/FinalPage/components/DevFinalValidationFlow").then(m => ({ default: m.DevFinalValidationFlow })));
const DevFinalThankYouFlow = lazyWithReload(() => import("./components/FinalPage/components/DevFinalThankYouFlow").then(m => ({ default: m.DevFinalThankYouFlow })));
const DevPendingFlow = lazyWithReload(() => import("./components/Congratulation/DevPendingFlow").then(m => ({ default: m.DevPendingFlow })));
const UnsubscribePage = lazyWithReload(() => import("./components/UnsubscribePage").then(m => ({ default: m.UnsubscribePage })));
const SuccessWheel = lazyWithReload(() => import("./components/SuccessWheel").then(m => ({ default: m.SuccessWheel })));
const BeneficiosPage = lazyWithReload(() => import("./components/Beneficios").then(m => ({ default: m.BeneficiosPage })));
const OfferwallPage = lazyWithReload(() => import("./components/OfferwallPage").then(m => ({ default: m.OfferwallPage })));
const SafePage = lazyWithReload(() => import("./components/SafePage").then(m => ({ default: m.SafePage })));
const RatePage = lazyWithReload(() => import("./components/RatePage").then(m => ({ default: m.RatePage })));
const PizzaPage = lazyWithReload(() => import("./components/PizzaPage").then(m => ({ default: m.PizzaPage })));
const DrumulScurtPage = lazyWithReload(() => import("./components/DrumulScurtPage").then(m => ({ default: m.DrumulScurtPage })));
const LeadOffersPage = lazyWithReload(() => import("./components/LeadOffersPage").then(m => ({ default: m.LeadOffersPage })));

export function App() {
  const clickId = useAppSelector(state => state.userData.clickID);
  const dispatch = useAppDispatch();

  // Ref для отслеживания, что конфиги уже запрошены (предотвращает дублирование)
  const configsRequested = useRef(false);

  useEffect(() => {
    // Setting user's clickID
    setClickIDHandler(dispatch);
  }, [dispatch]);

  useEffect(() => {
    const affiliate = resolveAffiliateFromSearch();
    if (affiliate) {
      dispatch(setAffiliateId(affiliate));
    }
    // Для статистики страницы ожидания: реальные affiliate/subaccount из ссылки входа.
    rememberTrafficSource();
  }, [dispatch]);

  const iframeRedirect = (event: any) => {
    if (event.data.type === 'REDIRECT') {
      trackRedirect("iframe_post_message", { targetUrl: event.data.url, extra: { origin: event.origin } });
      window.location.href = event.data.url;
    }
  }

  useEffect(() => {
    const variant = process.env.REACT_APP_VARIANT ?? '';
    setSiteMeta(variant);
  }, []);

  // Загрузка конфигов выполняется строго один раз при монтировании
  // Не зависит от clickId, чтобы форма работала даже без аналитики (инкогнито)
  useEffect(() => {
    if (!configsRequested.current) {
      configsRequested.current = true;
      dispatch(getGeneralConfig());
      dispatch(getFormsConfig());
    }
  }, [dispatch]);

  // Отправка аналитики выполняется только когда clickId доступен
  useEffect(() => {
    if (clickId) {
      sendQueryParams(clickId, window.location.search);
    }
  }, [clickId]);

  useEffect(() => {
    window.addEventListener('message', iframeRedirect);

    return () => {
      window.removeEventListener('message', iframeRedirect);
    }
  }, []);

  useEffect(() => {
    const handleResourceError = (event: any) => {
      if (event?.target?.tagName) {
        const tag = event.target.tagName.toLowerCase();
        if (['script', 'img', 'link', 'iframe'].includes(tag)) {
          const src = event.target.src || event.target.href || 'unknown';
          const errorType = `${tag}_load_failed`;

          // Фильтруем ошибки изображений с пустыми или некорректными URL
          if (tag === 'img') {
            if (!src || src === 'unknown' || src === '()' || src.trim() === '' || src === 'undefined') {
              return; // Пропускаем пустые изображения
            }
          }

          // Фильтруем ошибки к тестовым API доменам
          if (src && src.includes('.test.api.leadsor.com')) {
            return; // Пропускаем тестовые API ошибки
          }

          // Получаем дополнительную информацию об ошибке
          const errorMessage = event.error?.message || event.message || 'Unknown error';
          const fileName = src.split('/').pop() || 'unknown';

          // Определяем тип ресурса
          let resourceType = tag;
          if (tag === 'link') {
            const rel = event.target.rel;
            if (rel?.includes('stylesheet')) resourceType = 'stylesheet';
            else if (rel?.includes('icon')) resourceType = 'favicon';
            else resourceType = 'link';
          }

          // Отправляем в нашу систему мониторинга редиректов с дополнительной информацией
          trackResourceLoadError(src, errorType, {
            fileName,
            resourceType,
            errorMessage,
            // HTTP статус может быть недоступен в error event, но добавляем для полноты
            httpStatus: event.target.status || undefined
          });
        }
      }
    };

    const handleNavigationError = (event: any) => {
      // Отслеживаем проблемы с навигацией
      if (event.type === 'error' && event.target === window) {
        trackInAppBrowserIssue('navigation_blocked', {
          error: event.error?.message || 'Unknown navigation error',
          url: window.location.href
        });
      }
    };

    // Улучшенный обработчик для fetch ошибок
    const handleFetchError = (event: any) => {
      if (event.type === 'unhandledrejection' && event.reason) {
        const reason = event.reason;

        // Проверяем, является ли это ошибкой fetch/API
        if (reason instanceof TypeError && reason.message.includes('fetch')) {
          let resourceUrl = 'unknown';
          let errorType = 'fetch_load_failed';
          let errorDetails = {
            fileName: 'api-endpoint',
            resourceType: 'api',
            errorMessage: reason.message
          };

          // Пытаемся извлечь URL из сообщения об ошибке
          const urlMatch = reason.message.match(/https?:\/\/[^\s]+/);
          if (urlMatch) {
            resourceUrl = urlMatch[0];
            errorDetails.fileName = urlMatch[0].split('/').pop() || 'api-endpoint';

            // Фильтруем ошибки к тестовым API доменам
            if (resourceUrl.includes('.test.api.leadsor.com')) {
              return; // Пропускаем тестовые API ошибки
            }
          }

          // Определяем тип ошибки на основе сообщения
          if (reason.message.includes('Failed to fetch')) {
            errorType = 'network_error';
            errorDetails.errorMessage = 'Network error - failed to fetch resource';
          } else if (reason.message.includes('fetch')) {
            errorType = 'fetch_error';
          }

          trackResourceLoadError(resourceUrl, errorType, errorDetails);
        }
      }
    };

    window.addEventListener('error', handleResourceError, true);
    window.addEventListener('error', handleNavigationError);
    window.addEventListener('unhandledrejection', handleFetchError);

    return () => {
      window.removeEventListener('error', handleResourceError, true);
      window.removeEventListener('error', handleNavigationError);
      window.removeEventListener('unhandledrejection', handleFetchError);
    };
  }, []);

  return (
    <BrowserRouter>
      <LandingViewTracker />
      <ScrollDepthTracker />
      <EngagementTimeTracker />
      <SpentTimeTracker />
      <Suspense fallback={<Preloader />}>
        <Routes>
          <Route path="/" element={<Layout><FirstPage /></Layout>} />
          <Route path={`/${getFormLink()}`} element={<Layout><CustomFormContainer /></Layout>} />
          <Route path={AUTO_REGISTER} element={<AutoRegister />} />
          <Route path={PENDING} element={<Congratulation />} />
          <Route path="/:seoPageHtml" element={<Layout><FirstPage /></Layout>} />
          {/* Старые ссылки на /offers воронки уводят на офферволл во frontend-machine. */}
          <Route path={OFFERS} element={<OfferwallRedirect />} />
          <Route path={FINAL} element={<FinalPage />} />
          <Route path={INFO} element={<Informance />} />
          <Route path={COOKIES} element={<Cookies />} />
          <Route path={DEV_FINAL_VALIDATION_FLOW} element={<Layout><DevFinalValidationFlow /></Layout>} />
          <Route path={DEV_FINAL_THANKYOU_FLOW} element={<Layout><DevFinalThankYouFlow /></Layout>} />
          <Route path={DEV_PENDING_FLOW} element={<DevPendingFlow />} />
          <Route path={`/${UNSUBSCRIBE}`} element={<UnsubscribePage />} />
          <Route path={`/${SUCCESS}`} element={<SuccessWheel />} />
          <Route path={`/${BENEFICIOS}`} element={<BeneficiosPage />} />
          <Route path={`/${OFFERWALL_LANDING}`} element={<OfferwallPage />} />
          <Route path={`/${SAFE_LANDING}`} element={<SafePage />} />
          <Route path={`/${RATE_LANDING}`} element={<RatePage />} />
          <Route path={`/${PIZZA_LANDING}`} element={<PizzaPage />} />
          <Route path={`/${DRUMUL_SCURT_LANDING}`} element={<DrumulScurtPage />} />
          <Route path={`/${LEAD_OFFERS}`} element={<LeadOffersPage />} />
          <Route path="/leadsor.frontend" element={<Navigate to="/" replace />} />

          <Route path='*' element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}
